/**
 * 弹出页：手机上是底部抽屉，桌面端（≥900px）是居中对话框。
 *   const [open, setOpen] = useState(false)
 *   <Sheet open={open} onClose={() => setOpen(false)}>
 *     <ModalHeader title="新建日程" actionTitle="保存" onCancel={...} onAction={...} />
 *     ...内容
 *   </Sheet>
 */
import { X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "./Button";

let openCount = 0;
/** 打开着的弹出页（后打开的在后面）：按 Esc 只关最上面那个（弹出页里再弹出选择页、确认框时） */
const escStack: symbol[] = [];

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** 桌面端对话框宽度，默认 520 */
  width?: number;
  /** 点遮罩不关闭（表单有未保存内容时） */
  persistent?: boolean;
  /** 无障碍标题 */
  label?: string;
}

export function Sheet({ open, onClose, children, width = 520, persistent, label }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    openCount++;
    document.body.classList.add("kp-no-scroll");
    const prevFocus = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const token = Symbol("sheet");
    escStack.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && escStack[escStack.length - 1] === token) closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      escStack.splice(escStack.indexOf(token), 1);
      if (--openCount === 0) document.body.classList.remove("kp-no-scroll");
      prevFocus?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="kp-sheet-root" onMouseDown={(e) => e.target === e.currentTarget && !persistent && onClose()}>
      <div ref={panel} className="kp-sheet" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} style={{ ["--kp-sheet-w" as string]: `${width}px` }}>
        <div className="kp-sheet-handle" aria-hidden />
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** 弹出页顶部：标题 + 右侧关闭按钮 */
export function SheetHeader({ title, onClose, trailing }: { title: ReactNode; onClose?: () => void; trailing?: ReactNode }) {
  return (
    <div className="kp-sheet-header">
      <h2>{title}</h2>
      {trailing}
      {onClose && (
        <button type="button" className="kp-icon-btn" aria-label="关闭" onClick={onClose} style={{ width: 36, height: 36 }}>
          <X size={18} strokeWidth={2.6} />
        </button>
      )}
    </div>
  );
}

/** 弹出页顶部：取消 · 标题 · 主按钮（与 iOS ModalHeader 一致） */
export function ModalHeader({
  title,
  cancelTitle = "取消",
  actionTitle,
  actionEnabled = true,
  busy,
  onCancel,
  onAction,
}: {
  title: ReactNode;
  cancelTitle?: string;
  actionTitle: string;
  actionEnabled?: boolean;
  busy?: boolean;
  onCancel: () => void;
  onAction: () => void;
}) {
  return (
    <div className="kp-modal-header">
      <button type="button" className="kp-modal-cancel" onClick={onCancel}>
        {cancelTitle}
      </button>
      <h2>{title}</h2>
      <button type="button" className="kp-modal-action" disabled={!actionEnabled || busy} onClick={onAction}>
        {busy ? <Spinner size={16} /> : actionTitle}
      </button>
    </div>
  );
}

/** 弹出页的内容区（统一内边距，内容多时滚动） */
export function SheetBody({ children, gap = 14 }: { children: ReactNode; gap?: number }) {
  return (
    <div className="kp-sheet-body" style={{ gap }}>
      {children}
    </div>
  );
}
